import React, { useEffect, useState } from "react";
import { Phone, X, Grid3x3, Users, Settings2, Delete } from "lucide-react";
import { apiClient } from "../../lib/api";
import { useSoftphone } from "./useSoftphone";
import { useIncomingCalls } from "./useIncomingCalls";
import { IncomingCallPopup } from "./IncomingCallPopup";
import { ActiveCall } from "./ActiveCall";
import { Keypad } from "./Keypad";
import { PhoneSettings } from "./PhoneSettings";
import "./softphone.css";

const Contacts = ({ onCall }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { apiClient.get("/call-center/directory").then(({ data }) => setRows(Array.isArray(data) ? data : [])).catch(() => {}); }, []);
  const list = rows.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()) || r.extension.includes(q));
  return (
    <div>
      <input className="sp-input sp-full" value={q} onChange={(e) => setQ(e.target.value)} placeholder="search teammate or extension" data-testid="softphone-contacts-search" />
      <div className="sp-contacts">
        {list.length === 0 && <div className="sp-note">no teammates match</div>}
        {list.map((r) => (
          <button key={r.id} className="sp-contact" onClick={() => onCall({ extension: r.extension }, r.name)} data-testid={`softphone-contact-${r.id}`}>
            <span className={`sp-dot ${r.on_duty ? "on" : ""}`} />
            <span className="sp-contact-name">{r.name}</span>
            <span className="sp-ext">ext. {r.extension}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default function Softphone() {
  const phone = useSoftphone();
  const { incoming, dismiss } = useIncomingCalls(true);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("keypad");
  const [number, setNumber] = useState("");
  const [prefs, setPrefs] = useState(null);
  const [note, setNote] = useState(null);

  useEffect(() => { apiClient.get("/softphone/prefs").then(({ data }) => setPrefs(data)).catch(() => {}); }, []);
  useEffect(() => { if (phone.call) setOpen(true); }, [phone.call]);

  const place = async (target, label) => {
    setNote(null);
    if (prefs?.ring_mode === "cell_first") {
      try {
        await apiClient.post("/call-center/dial", target.extension ? { extension: target.extension, name: label } : { to_phone: target.to_phone, name: label });
        setNote("Your cell is ringing — answer it to be connected.");
      } catch (e) { setNote(e?.response?.data?.detail || "call failed to start"); }
      return;
    }
    phone.dial(target);
  };

  const dialNumber = () => {
    const v = number.trim();
    if (/^\d{3}$/.test(v)) return place({ extension: v }, `ext. ${v}`);
    if (v.replace(/\D/g, "").length < 10) { setNote("enter a 10-digit number or a 3-digit extension"); return; }
    place({ to_phone: v }, v);
  };

  const inCall = !!phone.call;
  return (
    <>
      <IncomingCallPopup incoming={inCall ? [] : incoming} onAnswer={phone.answer} dismiss={dismiss} busy={inCall} />
      <button className={`sp-fab ${inCall ? "live" : ""} ${incoming.length && !inCall ? "ringing" : ""}`} onClick={() => setOpen((o) => !o)} data-testid="softphone-fab" title="Phone">
        {open ? <X size={22} /> : <Phone size={22} />}
      </button>
      {open && (
        <div className="sp-panel" data-testid="softphone-panel">
          <div className="sp-head">
            <span>Phone</span>
            {prefs?.extension && <span className="sp-ext" data-testid="softphone-my-extension">my ext. {prefs.extension}</span>}
          </div>
          {inCall ? <ActiveCall phone={phone} /> : (
            <>
              <div className="sp-tabs">
                {[["keypad", Grid3x3, "Keypad"], ["contacts", Users, "Contacts"], ["settings", Settings2, "Settings"]].map(([k, Icon, l]) => (
                  <button key={k} className={`sp-tab ${tab === k ? "on" : ""}`} onClick={() => setTab(k)} data-testid={`softphone-tab-${k}`}><Icon size={14} />{l}</button>
                ))}
              </div>
              <div className="sp-body">
                {tab === "keypad" && (
                  <div>
                    <div className="sp-row">
                      <input className="sp-input sp-number" value={number} onChange={(e) => setNumber(e.target.value)} onKeyDown={(e) => e.key === "Enter" && dialNumber()} placeholder="number or extension" data-testid="softphone-number-input" />
                      <button className="sp-icon" onClick={() => setNumber((n) => n.slice(0, -1))} data-testid="softphone-backspace-btn"><Delete size={16} /></button>
                    </div>
                    <Keypad onKey={(k) => setNumber((n) => n + k)} testPrefix="softphone-pad" />
                    <button className="sp-btn-round sp-green sp-call" onClick={dialNumber} data-testid="softphone-call-btn" title="Call"><Phone size={20} /></button>
                  </div>
                )}
                {tab === "contacts" && <Contacts onCall={place} />}
                {tab === "settings" && prefs && <PhoneSettings prefs={prefs} setPrefs={setPrefs} />}
              </div>
              {prefs?.ring_mode === "cell_first" && tab !== "settings" && <div className="sp-hint">Calls ring your cell first (change in Settings)</div>}
            </>
          )}
          {!inCall && phone.status !== "idle" && <div className={`sp-note sp-st-${phone.status}`} data-testid="softphone-last-status">Last call: {phone.status.replace("_", " ")}</div>}
          {(note || phone.error) && <div className="sp-note sp-err" data-testid="softphone-note">{note || phone.error}</div>}
        </div>
      )}
      <audio ref={phone.audioRef} autoPlay data-testid="softphone-remote-audio" />
    </>
  );
}
